<template>
  <div class="w-full h-full flex flex-col min-h-0">
    <div class="w-full flex-1 min-h-0 relative">
      <GenericChart
        v-if="chartConfig && chartConfig.type === 'boxplot'"
        type="boxplot"
        :data="getTranslatedChartData(chartConfig)"
        :options="boxplotChartOptions"
        class="w-full h-full"
      />
      <div
        v-else
        class="flex items-center justify-center h-full text-gray-500"
      >
        {{ $t('BarChartComponent.noDataAvailable') || 'No data available' }}
      </div>
    </div>
  </div>
</template>

<script>
import { Chart as GenericChart } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';

ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  BoxPlotController,
  BoxAndWiskers,
);

export default {
  name: 'BoxPlotComponent',
  components: {
    GenericChart,
  },
  props: {
    chartConfig: {
      type: Object,
      required: true,
    },
    dimensions: {
      type: Array,
      default: () => [],
    },
    /** Optional footer below stats (e.g. data reliability) — team dashboard only */
    tooltipFooterText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this._boxplotTooltipEl?.parentNode) {
      this._boxplotTooltipEl.parentNode.removeChild(this._boxplotTooltipEl);
    }
    this._boxplotTooltipEl = null;
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    useLikertScale() {
      const y = this.chartConfig?.options?.scales?.y;
      if (!y || y.min !== 0) return false;
      const max = y.max ?? y.suggestedMax;
      return max === 5 || max === 6;
    },
    showLegend() {
      const n = this.chartConfig?.data?.datasets?.length || 0;
      const fromConfig = this.chartConfig?.options?.plugins?.legend?.display;
      if (typeof fromConfig === 'boolean') return fromConfig;
      return n > 1;
    },
    boxplotChartOptions() {
      const cfg = this.chartConfig?.options || {};
      const yFromCfg = cfg.scales?.y || {};
      const likert = this.useLikertScale;
      const meanBorderColor =
        cfg.elements?.boxandwhiskers?.meanBorderColor
        ?? this.getPrimaryDatasetBorderColor()
        ?? '#000000';
      /** Upper Likert bound from config (1–5 or 1–6 charts); used for tick labels */
      const likertYMax = likert
        ? Number(yFromCfg.max ?? yFromCfg.suggestedMax ?? 5) || 5
        : 5;
      return {
        responsive: true,
        maintainAspectRatio: false,
        barPercentage: 0.5,
        categoryPercentage: 0.6,
        maxBarThickness: 20,
        layout: {
          padding: { top: 25, bottom: 8, left: 4, right: 4 },
          ...cfg.layout,
        },
        plugins: {
          ...cfg.plugins,
          legend: {
            ...(cfg.plugins?.legend || {}),
            display: this.showLegend,
          },
          tooltip: {
            ...(cfg.plugins?.tooltip || {}),
            enabled: false,
            // Arrow fn: Chart.js calls external with TooltipModel as `this`; we need Vue `this`.
            external: (ctx) => this.renderBoxplotExternalTooltip(ctx),
          },
        },
        elements: {
          ...(cfg.elements || {}),
          boxandwhiskers: {
            ...(cfg.elements?.boxandwhiskers || {}),
            meanRadius: 3,
            meanBorderColor: meanBorderColor,
          },
        },
        scales: {
          x: {
            type: 'category',
            offset: true,
            ...cfg.scales?.x,
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              font: { size: this.isMobile ? 8 : 9 },
              maxTicksLimit: 20,
              autoSkip: false,
              ...cfg.scales?.x?.ticks,
              callback: (tickValue, index) => {
                const labels = this.getTranslatedChartData(this.chartConfig)?.labels;
                const label = (labels && index >= 0 && index < labels.length)
                  ? String(labels[index])
                  : (tickValue != null && tickValue !== '' ? String(tickValue) : '');
                const maxCharsPerLine = this.isMobile ? 8 : 12;
                const lines = this.formatLabel(label, maxCharsPerLine);
                return lines.length > 0 ? lines : '';
              },
            },
            grid: { display: !this.isMobile, ...cfg.scales?.x?.grid },
          },
          y: {
            min: 0,
            suggestedMax: 5,
            grace: '5%',
            ...yFromCfg,
            // Likert: fixed 0…max, no grace — same idea as bar charts (full scale, all step ticks)
            ...(likert
              ? {
                  min: yFromCfg.min ?? 0,
                  max: likertYMax,
                  grace: 0,
                }
              : {}),
            ticks: {
              font: { size: this.isMobile ? 9 : 11 },
              stepSize: 1,
              ...yFromCfg.ticks,
              ...(likert ? { autoSkip: false, stepSize: 1 } : {}),
              callback: (value) => {
                if (!likert) {
                  const cb = yFromCfg.ticks?.callback;
                  return typeof cb === 'function' ? cb(value) : value;
                }
                const v = Number(value);
                if (this.isMobile) {
                  if (v >= 1 && v <= likertYMax && Number.isInteger(v)) return String(v);
                  return '';
                }
                if (v >= 1 && v <= likertYMax && Number.isInteger(v)) {
                  return this.getLikertYAxisLabel(v);
                }
                return '';
              },
            },
          },
        },
      };
    },
  },
  methods: {
    escapeHtml(text) {
      if (text == null || text === '') return '';
      const d = document.createElement('div');
      d.textContent = String(text);
      return d.innerHTML;
    },
    populationStdDev(values, mean) {
      const nums = values.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
      if (nums.length === 0) return null;
      const m = mean != null && !Number.isNaN(Number(mean))
        ? Number(mean)
        : nums.reduce((a, b) => a + b, 0) / nums.length;
      const n = nums.length;
      const variance = nums.reduce((s, x) => s + (x - m) ** 2, 0) / n;
      return Math.sqrt(variance);
    },
    resolveStdDev(raw, parsed, mean) {
      const r = raw && typeof raw === 'object' ? raw : {};
      if (typeof r.sd === 'number' && !Number.isNaN(r.sd)) return r.sd;
      const items = Array.isArray(r.items) ? r.items : (Array.isArray(parsed?.items) ? parsed.items : null);
      if (items && items.length > 0) {
        return this.populationStdDev(items, mean);
      }
      return null;
    },
    /**
     * Custom HTML tooltip: title "Values — dimension", label/value rows, optional footer.
     */
    renderBoxplotExternalTooltip(context) {
      const { chart, tooltip } = context;
      let el = this._boxplotTooltipEl;

      if (!el) {
        el = document.createElement('div');
        el.className = 'boxplot-html-tooltip pointer-events-none fixed z-[9999] min-w-[100px] max-w-[200px] rounded-md border border-gray-100 bg-white px-2 py-1.5 text-xs shadow-md';
        el.setAttribute('role', 'tooltip');
        document.body.appendChild(el);
        this._boxplotTooltipEl = el;
      }

      if (!chart?.canvas || tooltip.opacity === 0) {
        if (el) {
          el.style.opacity = '0';
          el.style.visibility = 'hidden';
        }
        return;
      }

      const dp = tooltip.dataPoints?.[0];
      if (!dp) {
        el.style.opacity = '0';
        return;
      }

      const raw = dp.raw;
      const parsed = dp.parsed;
      const stats = raw && typeof raw === 'object' ? raw : {};
      const p = parsed && typeof parsed === 'object' ? parsed : {};
      const fmt = (v) => (v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : '–');
      const mean = stats.mean ?? p.mean;
      const sd = this.resolveStdDev(stats, p, mean);

      const t = (key, fallback) => this.$t(`BoxPlotTooltip.${key}`) || fallback;
      const rows = [
        { label: t('mean', 'Mean'), value: fmt(stats.mean ?? p.mean) },
        { label: t('median', 'Median'), value: fmt(stats.median ?? p.median) },
        { label: t('quartile1', '1st quartile (Q1)'), value: fmt(stats.q1 ?? p.q1) },
        { label: t('quartile3', '3rd quartile (Q3)'), value: fmt(stats.q3 ?? p.q3) },
        { label: t('minimum', 'Minimum'), value: fmt(stats.min ?? p.min) },
        { label: t('maximum', 'Maximum'), value: fmt(stats.max ?? p.max) },
        { label: t('standardDeviation', 'Standard deviation (σ)'), value: sd != null ? fmt(sd) : '–' },
      ];

      const translated = this.getTranslatedChartData(this.chartConfig);
      const dimLabel = translated?.labels?.[dp.dataIndex] ?? '';
      const heading = this.$t('BoxPlotTooltip.valuesHeading') || 'Values';
      const titleText = `${heading} — ${dimLabel}`;

      const footer = (this.tooltipFooterText || '').trim();

      let bodyHtml = `<div class="font-semibold text-gray-900 mb-1 text-[11px] leading-tight">${this.escapeHtml(titleText)}</div>`;
      bodyHtml += '<div class="space-y-0.5">';
      rows.forEach((row) => {
        bodyHtml += '<div class="flex justify-between gap-2 items-baseline text-[11px] leading-tight">'
          + `<span class="text-gray-500 shrink-0">${this.escapeHtml(row.label)}</span>`
          + `<span class="font-semibold text-gray-900 tabular-nums text-right">${row.value}</span>`
          + '</div>';
      });
      bodyHtml += '</div>';

      if (footer) {
        bodyHtml += '<hr class="my-1.5 border-gray-200" />'
          + `<div class="text-[10px] leading-snug text-gray-400">${this.escapeHtml(footer)}</div>`;
      }

      el.innerHTML = bodyHtml;
      el.style.opacity = '1';
      el.style.visibility = 'visible';

      const rect = chart.canvas.getBoundingClientRect();
      const cx = tooltip.caretX ?? 0;
      const cy = tooltip.caretY ?? 0;
      el.style.left = `${rect.left + cx}px`;
      el.style.top = `${rect.top + cy}px`;
      el.style.transform = 'translate(-50%, calc(-100% - 6px))';
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    formatLabel(str, maxwidth) {
      const sections = [];
      const words = str.split(' ');
      let temp = '';

      words.forEach((item, index) => {
        if (temp.length > 0) {
          const concat = temp.concat(' ').concat(item);

          if (concat.length > maxwidth) {
            sections.push(temp);
            temp = '';
          } else {
            if (index === (words.length - 1)) {
              sections.push(concat);
              return;
            }
            temp = concat;
            return;
          }
        }

        if (index === (words.length - 1)) {
          sections.push(item);
          return;
        }

        if (item.length < maxwidth) {
          temp = item;
        } else {
          sections.push(item);
        }
      });
      return sections;
    },
    getDimensionLabel(dimensionValue) {
      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },
    getLikertYAxisLabel(value) {
      const keyMap15 = {
        1: 'SliderQuestion.datalistOptions.stronglyDisagree',
        2: 'SliderQuestion.datalistOptions.disagree',
        3: 'SliderQuestion.datalistOptions.indifferent',
        4: 'SliderQuestion.datalistOptions.agree',
        5: 'SliderQuestion.datalistOptions.stronglyAgree',
      };
      const key = keyMap15[value];
      return key ? this.$t(key) : String(value);
    },
    normalizeColor(color) {
      if (!color) return color;
      if (Array.isArray(color)) {
        return color.map((c) => this.normalizeColor(c));
      }
      if (typeof color === 'string') {
        if (color.startsWith('#') || color.startsWith('rgb')) return color;
        return `#${color}`;
      }
      return color;
    },
    getPrimaryDatasetBorderColor() {
      const ds = this.chartConfig?.data?.datasets?.[0];
      if (!ds?.borderColor) return null;
      const bc = ds.borderColor;
      if (Array.isArray(bc)) {
        const first = bc.find((c) => c != null && c !== '');
        return first != null ? this.normalizeColor(first) : null;
      }
      return this.normalizeColor(bc);
    },
    getTranslatedChartData(chartConfig) {
      if (!chartConfig || !chartConfig.data) {
        return chartConfig;
      }

      const translatedLabels = chartConfig.data.labels
        .map((label) => this.getDimensionLabel(label));

      return {
        ...chartConfig.data,
        labels: translatedLabels,
        datasets: chartConfig.data.datasets.map((dataset) => ({
          ...dataset,
          data: dataset.data,
          backgroundColor: this.normalizeColor(dataset.backgroundColor),
          borderColor: this.normalizeColor(dataset.borderColor),
        })),
      };
    },
  },
};
</script>
